import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import NoData from '@root/components/common/NoData';
import FunnelChart from '@root/components/FunnelChart';
import SeverityIcons from '@root/components/SeverityIcons/SeverityIcons';
import BasicContentBox from '@root/components/v2/content/BasicContentBox';
import Tooltip from '@root/components/v2/ui-elements/Tooltip';
import { useTheme } from '@root/context/ThemeContext';
import { Bug, CircleAlert, CirclePlay, Gauge, Globe, Info, Wrench } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { DRILLABLE_FUNNEL_STAGES } from '../funnelStageFilters';
import CardTitle from './CardTitle';

// The Kubernetes vulnerabilities "Runtime exploit funnel" card. Same stat-row layout as the
// threat dashboard's ThreatFunnelCard: an icon + count + label row above a tapering band, with the
// library's own labels switched off so each stat sits over the start of its stage. The band is split
// into CVSS severity layers (Critical / High / Other), keyed by the legend in the header.

// Severity layers, drawn top to bottom: the library stacks sub-labels in order, so Other (lightest)
// sits on top and Critical (darkest) at the bottom. Other folds medium, low and unknown together.
const BANDS = [
  { key: 'other', label: 'Other', color: '#FFC2AD', value: stage => stage.medium + stage.low + stage.unknown },
  { key: 'high', label: 'High', color: '#FF8A65', value: stage => stage.high },
  { key: 'critical', label: 'Critical', color: '#FF5722', value: stage => stage.critical },
];
const LEGEND_BANDS = [...BANDS].reverse();
const BAND_LABELS = BANDS.map(band => band.label);
const BAND_COLORS = BANDS.map(band => band.color);

const BAND_HEIGHT = 80;
const CARD_HEIGHT = '194px';
// Horizontal padding (and borders) around the band, subtracted from the measured card width.
const CARD_GUTTER = 34;

// Display label and accent per funnel stage key. The stages render in the order the API sends them; a key
// missing here falls back to its title-cased name.
const STAGES = [
  { key: 'total', label: 'Total', icon: Bug, color: '#FF7A45' },
  { key: 'in_use', label: 'Package in use', icon: CirclePlay, color: '#22A06B' },
  { key: 'internet_traffic', label: 'Internet exposure', icon: Globe, color: '#3BA4E8' },
  // The Workload vulnerabilities funnel's key for the same stage.
  { key: 'internet_exposure', label: 'Internet exposure', icon: Globe, color: '#3BA4E8' },
  { key: 'exploitable', label: 'Known exploit', icon: CircleAlert, color: '#E5484D' },
  { key: 'fixable', label: 'Fix available', icon: Wrench, color: '#F59E0B' },
  { key: 'critical', label: 'Critical', severity: 'critical' },
  // A gauge, not bars: EPSS is a predicted exploit probability, and bars read as the Critical stage's severity icon.
  { key: 'epss_severe', label: 'Epss Critical', icon: Gauge, color: '#8B5CF6' },
];
// The API sends seven stages.
const SKELETON_STAGE_COUNT = 7;
const STAGE_BY_KEY = new Map(STAGES.map(stage => [stage.key, stage]));

const fallbackStage = key => ({
  key,
  label: key.replace(/_/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase()),
  icon: CircleAlert,
  color: '#A0AEC0',
});

const formatPercent = value => `${Math.round(value * 1000) / 10}%`;

// Without the runtime sensor on any selected cluster, nothing is known to be in use: the API counts the
// stages without that condition, and the in-use stage shows as not available.
const isUnavailable = (key, data) => key === 'in_use' && data?.in_use_enabled === false;

// Per-stage info tooltip, from the API's metadata where the stage definition depends on it.
const stageInfo = (key, data) => {
  if (isUnavailable(key, data)) return 'Runtime sensor not enabled for the selected clusters';
  if (key === 'epss_severe' && data?.epss_threshold != null) {
    return `CVEs with an EPSS score of ${formatPercent(data.epss_threshold)} or more: at least that probability of being exploited in the next 30 days.`;
  }
  return null;
};

const toStages = data =>
  (Array.isArray(data?.stages) ? data.stages : []).map(counts => {
    const stage = STAGE_BY_KEY.get(counts.key) ?? fallbackStage(counts.key);
    const severities = {
      critical: counts.critical ?? 0,
      high: counts.high ?? 0,
      medium: counts.medium ?? 0,
      low: counts.low ?? 0,
      unknown: counts.unknown ?? 0,
    };
    return {
      ...stage,
      ...severities,
      value: counts.count ?? 0,
      bands: BANDS.map(band => band.value(severities)),
      info: stageInfo(stage.key, data),
      unavailable: isUnavailable(stage.key, data),
    };
  });

const CARD_STYLES = { height: CARD_HEIGHT, display: 'flex', flexDirection: 'column' };

const INNER_STYLES = {
  height: 'calc(100% - 40px)',
  display: 'flex',
  flexDirection: 'column',
  // justifyContent: 'space-between',
  padding: '12px 16px 16px',
  backgroundColor: 'var(--content-box-bg, #ffffff)',
  overflow: 'hidden',
};

const TITLE = <CardTitle title="Runtime exploit funnel" subtitle="By severity" />;

const LEGEND = (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', paddingRight: '16px', fontSize: '12px' }}>
    <Box component="span" sx={{ color: 'var(--text-primary)', fontWeight: 500 }}>
      CVSS:
    </Box>
    {LEGEND_BANDS.map(band => (
      <Box key={band.key} sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
        <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: band.color }} />
        <Box component="span" sx={{ color: 'var(--text-secondary)' }}>
          {band.label}
        </Box>
      </Box>
    ))}
  </Box>
);

const StageIcon = ({ stage }) => {
  if (stage.severity) {
    return (
      <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}>
        <SeverityIcons severity={stage.severity} height="16px" width="16px" />
      </Box>
    );
  }
  const Icon = stage.icon;
  return <Icon size={15} color={stage.color} style={{ flexShrink: 0 }} />;
};

// Loading state: same shell and geometry as the loaded card, so nothing shifts when data lands.
const RuntimeExploitFunnelSkeleton = () => {
  const { isDarkMode } = useTheme();
  const skeletonColor = isDarkMode ? '#2d3748' : undefined;

  return (
    <BasicContentBox title={TITLE} rightSideContent={LEGEND} style={CARD_STYLES} innerStyles={INNER_STYLES}>
      <Box display="flex" gap="12px" flexShrink={0}>
        {Array.from({ length: SKELETON_STAGE_COUNT }, (_, index) => (
          <Box key={index} flex={1} display="flex" alignItems="center" gap="8px">
            <Skeleton variant="circular" width={15} height={15} sx={{ bgcolor: skeletonColor }} />
            <Skeleton variant="text" width="70%" height={22} sx={{ bgcolor: skeletonColor }} />
          </Box>
        ))}
      </Box>
      <Skeleton
        variant="rectangular"
        width="100%"
        height={BAND_HEIGHT}
        sx={{ borderRadius: '2px', flexShrink: 0, bgcolor: skeletonColor }}
      />
    </BasicContentBox>
  );
};

const RuntimeExploitFunnel = ({ data, isLoading = false, onStageClick = null }) => {
  const stages = useMemo(() => toStages(data), [data]);
  const values = useMemo(() => stages.map(stage => stage.bands), [stages]);
  const labels = useMemo(() => stages.map(stage => stage.label), [stages]);
  // An all-zero funnel has no widest stage to scale the band against (the library divides by it).
  const hasFindings = stages[0]?.value > 0;

  // The funnel library sizes its SVG with fixed attributes, so measure the card and redraw on
  // resize to keep the band spanning it and lined up with the stat row (see ThreatFunnelCard).
  const cardRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(0);

  useEffect(() => {
    const node = cardRef.current;
    if (!node) return undefined;

    const measure = () => setChartWidth(Math.max(0, node.clientWidth - CARD_GUTTER));
    measure();

    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [isLoading, hasFindings]);

  if (isLoading) return <RuntimeExploitFunnelSkeleton />;

  if (!hasFindings) {
    return (
      <BasicContentBox title={TITLE} rightSideContent={LEGEND} style={CARD_STYLES} innerStyles={INNER_STYLES}>
        <NoData message="No funnel data available" />
      </BasicContentBox>
    );
  }

  const statRow = (
    <Box sx={{ display: 'flex', gap: '12px', flexShrink: 0, marginBottom: '25px' }}>
      {stages.map(stage => {
        // Total is the whole set, so it doesn't drill down; neither does a stage that is not available.
        const isClickable = Boolean(onStageClick) && DRILLABLE_FUNNEL_STAGES.has(stage.key) && !stage.unavailable;

        return (
          <Box key={stage.key} sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Box
              component={isClickable ? 'button' : 'div'}
              type={isClickable ? 'button' : undefined}
              onClick={
                isClickable ? () => onStageClick({ key: stage.key, label: stage.label, value: stage.value }) : undefined
              }
              sx={{
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: 0,
                border: 0,
                background: 'transparent',
                textAlign: 'left',
                fontFamily: 'Manrope',
                color: 'var(--content-box-text, #1a202c)',
                cursor: isClickable ? 'pointer' : 'default',
                opacity: stage.unavailable ? 0.5 : undefined,
                '&:hover .RuntimeExploitFunnel__Label': isClickable ? { textDecoration: 'underline' } : undefined,
              }}>
              <StageIcon stage={stage} />
              <Box component="span" sx={{ fontSize: '16px', fontWeight: 700, lineHeight: 1.2, flexShrink: 0 }}>
                {stage.unavailable ? 'N/A' : stage.value.toLocaleString()}
              </Box>
              <Box
                component="span"
                className="RuntimeExploitFunnel__Label"
                sx={{
                  fontSize: '12px',
                  fontWeight: 500,
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                {stage.label}
              </Box>
            </Box>
            {stage.info && (
              // A plain span, not a Box: the Tooltip passes `width="100%"` to its child.
              <Tooltip title={stage.info} isIcon>
                <span style={{ display: 'inline-flex', flexShrink: 0, color: 'var(--text-secondary)' }}>
                  <Info size={13} />
                </span>
              </Tooltip>
            )}
          </Box>
        );
      })}
    </Box>
  );

  // The stat row replaces the library's labels, so those and the room it reserves for them are off.
  return (
    <Box
      ref={cardRef}
      sx={{
        '& .runtime-exploit-funnel': { background: 'transparent !important' },
        '& .svg-funnel-js__labels': { display: 'none !important' },
        '& .svg-funnel-js:not(.svg-funnel-js--vertical)': { padding: '0 !important' },
        '& .runtime-exploit-funnel .funnel-container': { padding: '0 !important' },
        '& .runtime-exploit-funnel .funnel-container svg': { margin: '0 !important' },
      }}>
      <FunnelChart
        title={TITLE}
        rightSideContent={LEGEND}
        labels={labels}
        subLabels={BAND_LABELS}
        values={values}
        colors={BAND_COLORS}
        width={chartWidth}
        height={BAND_HEIGHT}
        containerStyles={CARD_STYLES}
        innerStyles={INNER_STYLES}
        chartStyles={{ height: `${BAND_HEIGHT}px`, width: '100%', flexShrink: 0, overflow: 'hidden' }}
        header={statRow}
      />
    </Box>
  );
};

export default RuntimeExploitFunnel;
